import React from 'react';
import AdvancedSEOHead from "@/components/seo/AdvancedSEOHead";
import { absoluteUrl } from '@/lib/seo';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { LinkedinIcon, MailIcon, GlobeIcon } from 'lucide-react';

const AuteursCosmosWave = () => {
  const seoData = {
    title: "Équipe Cosmos Wave — Experts Amazon & IA | Bio auteurs",
    description: "Découvrez l'équipe d'experts Cosmos Wave : spécialistes Amazon Ads, SP-API, IA et e-commerce. Expérience prouvée, certifications officielles, résultats clients.",
    breadcrumbs: [
      { name: "Accueil", url: "/" },
      { name: "Auteurs", url: "/auteurs/cosmos-wave" }
    ],
    schema: {
      "@context": "https://schema.org",
      "@type": "ProfilePage",
      "mainEntity": {
        "@type": "Organization",
        "name": "Cosmos Wave",
        "url": "https://www.cosmos-wave.com",
        "description": "Agence spécialisée Amazon : Ads, IA, SEO fiches produits, Catalog Health",
        "foundingDate": "2020",
        "areaServed": "FR",
        "expertise": [
          "Amazon Advertising (SP/SB/SD/DSP)",
          "Intelligence Artificielle pour e-commerce",
          "SP-API et conformité données",
          "SEO Amazon et optimisation fiches"
        ]
      }
    }
  };

  const team = [
    {
      role: "Fondateur & Stratège IA",
      name: "Équipe Direction",
      expertise: ["Amazon Ads (SP/SB/SD/DSP)", "IA & Machine Learning", "SP-API & Data Engineering", "Stratégie e-commerce"],
      experience: "8+ années Amazon, ex-consultant BigTech",
      certifications: ["Amazon Ads Certified", "Google Cloud AI/ML", "AWS Solution Architect"],
      linkedin: "https://www.linkedin.com/company/cosmoswave",
      description: "Expert Amazon depuis 2016. Spécialisé dans l'automatisation IA des campagnes publicitaires et l'optimisation data-driven des performances e-commerce."
    },
    {
      role: "Lead Developer & SP-API",
      name: "Équipe Tech",
      expertise: ["SP-API Integration", "Python/JavaScript", "BigQuery & Analytics", "Automatisation"],
      experience: "6+ années développement e-commerce",
      certifications: ["Amazon SP-API Partner", "Google Cloud Professional", "Microsoft Azure AI"],
      linkedin: "https://www.linkedin.com/company/cosmoswave",
      description: "Développement d'outils propriétaires et d'agents IA pour l'optimisation Amazon. Expert en intégrations SP-API et analyse de données massives."
    },
    {
      role: "Amazon Ads Specialist",
      name: "Équipe Performance",
      expertise: ["Campaign Management", "DSP Programmatique", "Creative Strategy", "Performance Analysis"],
      experience: "5+ années Amazon Advertising",
      certifications: ["Amazon DSP Certified", "Amazon Retail Certified", "Google Ads Expert"],
      linkedin: "https://www.linkedin.com/company/cosmoswave",
      description: "Gestion de +50M€ de budgets publicitaires Amazon. Spécialisé dans l'optimisation ROI et les stratégies créatives high-performance."
    }
  ];

  return (
    <>
      <AdvancedSEOHead
        title="Équipe Cosmos Wave | Auteurs & Experts Amazon SPN"
        description="Découvrez l'équipe Cosmos Wave : experts Amazon certifiés SPN, spécialistes e-commerce et creative strategists. Profils et expertises détaillés."
        canonical={absoluteUrl("/auteurs/cosmos-wave")}
        ogImage="/og/default-1200x630.jpg"
        ogType="website"
        breadcrumbs={[
          { name: "Auteurs", url: "/auteurs" },
          { name: "Cosmos Wave", url: "/auteurs/cosmos-wave" }
        ]}
        jsonLd={[{
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Cosmos Wave",
          "url": absoluteUrl("/auteurs/cosmos-wave"),
          "description": "Agence Amazon partenaire SPN spécialisée dans la stratégie e-commerce Amazon, l'optimisation des fiches produits, Amazon Ads et la créativité de marque.",
          "foundingDate": "2021",
          "areaServed": "France",
          "expertise": ["Amazon SPN", "E-commerce Strategy", "Creative Design", "Data Analysis"]
        }]}
      />
      
      <div className="min-h-screen bg-gradient-to-b from-stone-950 via-stone-900 to-stone-950">
        {/* Hero Section */}
        <section className="relative overflow-hidden pt-24 pb-16">
          <div className="absolute inset-0 bg-gradient-to-r from-violet-600/20 via-orange-500/10 to-violet-600/20"></div>
          <div className="relative mx-auto max-w-6xl px-4">
            <div className="text-center">
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                Équipe Cosmos Wave
                <span className="block text-2xl md:text-4xl text-orange-400 font-normal mt-2">
                  Experts Amazon & IA
                </span>
              </h1>
              <p className="text-xl text-stone-300 max-w-3xl mx-auto mb-8">
                Une équipe d'experts passionnés, spécialisée dans l'optimisation Amazon via l'IA. 
                Certifications officielles, expérience terrain, résultats prouvés.
              </p>
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="py-16">
          <div className="mx-auto max-w-4xl px-4 text-center">
            <h2 className="text-3xl font-bold text-white mb-8">Notre mission</h2>
            <p className="text-lg text-stone-300 leading-relaxed">
              Cosmos Wave a été créée avec une vision claire : démocratiser l'intelligence artificielle 
              pour les vendeurs Amazon. Nous combinons expertise technique pointue et vision business 
              pour transformer les données en croissance mesurable. Notre approche data-driven et nos 
              outils propriétaires permettent à nos clients de dépasser leurs concurrents.
            </p>
          </div>
        </section>

        {/* Team Profiles */}
        <section className="py-16 bg-stone-900/50">
          <div className="mx-auto max-w-6xl px-4">
            <h2 className="text-3xl font-bold text-white text-center mb-12">Profils d'expertise</h2>
            <div className="grid gap-8">
              {team.map((member, index) => (
                <Card key={index} className="p-8 bg-stone-800/50 border-stone-700">
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="md:col-span-2">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-2xl font-bold text-white mb-2">{member.name}</h3>
                          <p className="text-orange-400 font-medium mb-3">{member.role}</p>
                          <p className="text-stone-300 leading-relaxed mb-4">{member.description}</p>
                        </div>
                      </div>
                      
                      <div className="mb-4">
                        <h4 className="text-white font-semibold mb-2">Expertise clé</h4>
                        <div className="flex flex-wrap gap-2">
                          {member.expertise.map((skill, skillIndex) => (
                            <Badge key={skillIndex} variant="secondary" className="bg-orange-500/20 text-orange-300 border-orange-500/30">
                              {skill}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <h5 className="text-white font-medium mb-1">Expérience</h5>
                          <p className="text-stone-400">{member.experience}</p>
                        </div>
                        <div>
                          <h5 className="text-white font-medium mb-1">Certifications</h5>
                          <ul className="text-stone-400 space-y-1">
                            {member.certifications.map((cert, certIndex) => (
                              <li key={certIndex}>• {cert}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col justify-between">
                      <div className="space-y-3">
                        <a 
                          href={member.linkedin} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 p-3 rounded-lg bg-stone-700/50 hover:bg-stone-700 transition-colors"
                        >
                          <LinkedinIcon className="h-5 w-5 text-blue-400" />
                          <span className="text-stone-300 text-sm">LinkedIn</span>
                        </a>
                        
                        <a 
                          href="mailto:contact@cosmos-wave.com"
                          className="flex items-center gap-3 p-3 rounded-lg bg-stone-700/50 hover:bg-stone-700 transition-colors"
                        >
                          <MailIcon className="h-5 w-5 text-orange-400" />
                          <span className="text-stone-300 text-sm">Contact direct</span>
                        </a>
                        
                        <a 
                          href="https://www.cosmos-wave.com"
                          className="flex items-center gap-3 p-3 rounded-lg bg-stone-700/50 hover:bg-stone-700 transition-colors"
                        >
                          <GlobeIcon className="h-5 w-5 text-green-400" />
                          <span className="text-stone-300 text-sm">Portfolio</span>
                        </a>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Certifications & Partenariats */}
        <section className="py-16">
          <div className="mx-auto max-w-6xl px-4">
            <h2 className="text-3xl font-bold text-white text-center mb-12">Certifications & Partenariats</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  title: "Amazon Partner",
                  description: "SPN & Advertising Certified",
                  icon: "🏆"
                },
                {
                  title: "SP-API Expert", 
                  description: "Conformité & Intégrations",
                  icon: "⚡"
                },
                {
                  title: "Google Cloud AI",
                  description: "Machine Learning Certified",
                  icon: "🤖"
                },
                {
                  title: "BigQuery Pro",
                  description: "Analytics & Data Engineering",
                  icon: "📊"
                }
              ].map((cert, index) => (
                <Card key={index} className="p-6 text-center bg-stone-800/50 border-stone-700">
                  <div className="text-4xl mb-4">{cert.icon}</div>
                  <h3 className="text-lg font-semibold text-white mb-2">{cert.title}</h3>
                  <p className="text-stone-400 text-sm">{cert.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Team */}
        <section className="py-16 bg-gradient-to-r from-orange-600/20 to-violet-600/20">
          <div className="mx-auto max-w-4xl px-4 text-center">
            <h2 className="text-3xl font-bold text-white mb-6">Collaborer avec notre équipe</h2>
            <p className="text-xl text-stone-300 mb-8">
              Vous souhaitez échanger avec nos experts ? Notre équipe est disponible pour 
              discuter de votre projet Amazon et explorer les opportunités d'optimisation IA.
            </p>
            <a 
              href="mailto:contact@cosmos-wave.com"
              className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 rounded-lg font-medium transition-colors"
            >
              <MailIcon className="h-5 w-5" />
              Contacter l'équipe
            </a>
          </div>
        </section>
      </div>
    </>
  );
};

export default AuteursCosmosWave;